// Shared option lists. Date-format tokens follow the company.date_format style
// (yyyy/MM/dd), with a worked example so admins can see what each renders as.
export const DATE_FORMATS = [
  { v: 'yyyy-MM-dd',   ex: '2026-06-30' },
  { v: 'dd/MM/yyyy',   ex: '30/06/2026' },
  { v: 'MM/dd/yyyy',   ex: '06/30/2026' },
  { v: 'dd-MM-yyyy',   ex: '30-06-2026' },
  { v: 'dd.MM.yyyy',   ex: '30.06.2026' },
  { v: 'dd MMM yyyy',  ex: '30 Jun 2026' },
  { v: 'dd MMMM yyyy', ex: '30 June 2026' },
];

// A tax RATE for display: drop trailing zeros so Postgres numeric "25.5000" → "25.5",
// "15.0000" → "15", "0.0000" → "0". NEVER round to a whole number — Finland's 25.5% / 13.5%
// rates turned into "26%" / "14%" under the old toFixed(0).
export function fmtRate(r){
  const n = Number(r);
  return Number.isFinite(n) ? String(n) : '0';
}

// Build a searchSelect option for a VAT/tax code. The document editor shows the RATE in the
// collapsed box (owner 2026-09-18: "important is to show the 25% not the type") — which works only
// while the rate uniquely identifies the code. A country can have SEVERAL codes at one rate: Finland
// has five at 0% (domestic zero-rated, EU goods, EU services, construction reverse-charge, exempt),
// which the rate alone cannot tell apart. So a zero/shared-rate code leads with its NAME and carries
// the rate as the hint; a rated code keeps the compact "25.5%" the owner asked for. The long text is
// always the `label` (the component ellipsises it and the VAT picker widens its popup) and never the
// fixed-width `sub`, so a name is never clipped to an unreadable stub.
export function taxCodeOption(tc){
  const pct = fmtRate(tc.rate) + '%';
  const keywords = (tc.code || '') + ' ' + (tc.name || '');
  return Number(tc.rate) > 0
    ? { value: String(tc.id), label: pct, sub: tc.name || '', keywords }
    : { value: String(tc.id), label: tc.name || pct, sub: pct, keywords };
}

// Parse a user-typed money amount, tolerant of decimal-separator conventions — South
// Africans type the comma decimal ("50,00"), statements paste with space/NBSP thousands
// ("1 234,56"), en-US uses "1,234.56". Naively stripping commas turned "50,00" into
// R 5 000.00 (a silent 100× corruption) — hence the rules:
//   · both "," and "." present → the LAST one typed is the decimal separator
//   · only "," present → decimal when it is one comma with 1–2 trailing digits, else thousands
//   · junk that survives cleaning → null (refuse; never a silent partial parse)
// `marks` are the marks of the currency the figure is in — its symbol and ISO code, e.g. ['€', 'EUR'].
// One of them may stand before or after the figure, or after a minus ("R 50.00", "-€5", "50,00 €",
// "EUR 50"), in any letter case. A mark of any OTHER currency — or any mark when none are given — is
// refused (null): "$ 50" typed for a euro account is not fifty euro, and nothing assumes the rand
// (owner 2026-10-19: "I DO NOT WANT CURRENCIES TO BLEED INTO EACH OTHER").
export function parseAmount(s, marks){
  let t = String(s == null ? '' : s).replace(/[\s  ']/g, '');   // spaces incl NBSP, apostrophe thousands
  const ms = [...new Set((marks || []).map(m => String(m == null ? '' : m).replace(/[\s  ]/g, '').toUpperCase())
    .filter(Boolean))].sort((a, b) => b.length - a.length);        // the longest first: "US$" before "$"
  const cut = atEnd => {
    const m = ms.find(x => x.length <= t.length
      && (atEnd ? t.slice(t.length - x.length) : t.slice(0, x.length)).toUpperCase() === x);
    if(m) t = atEnd ? t.slice(0, t.length - m.length) : t.slice(m.length);
  };
  let neg = t.startsWith('-');
  if(neg) t = t.slice(1);
  cut(false);
  if(!neg && t.startsWith('-')){ neg = true; t = t.slice(1); }  // "R-50"
  cut(true);
  if(!t || t.startsWith('-')) return null;                        // one minus only
  if(neg) t = '-' + t;
  const lastComma = t.lastIndexOf(','), lastDot = t.lastIndexOf('.');
  if(lastComma !== -1 && lastDot !== -1){
    t = lastComma > lastDot ? t.replace(/\./g, '').replace(/,/g, '.') : t.replace(/,/g, '');
  } else if(lastComma !== -1){
    const parts = t.split(',');
    t = (parts.length === 2 && parts[1].length <= 2) ? parts[0] + '.' + parts[1] : parts.join('');
  }
  if(!/^-?\d+(\.\d+)?$/.test(t)) return null;
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : null;
}
